<template>
  <DropdownTooltip
    placement="bottom-start"
    popperClass="reactions__popper"
    iconSize="none"
    class="reactions"
    :triggers="[]"
    :shown="shownUserList"
    @contextmenu.prevent="onShowListOfUsers"
    @onHideDropdown="onHideListOfUsers"
  >
    <template #tooltip-label>
      <transition-group
        name="fade-reactions"
        tag="div"
        class="reactions__list"
      >
        <div
          v-for="reaction in reactionKeys"
          :key="reaction"
          class="reactions__item"
          :class="{
            'is-selected': myReactionType === reaction,
            'has-avatars': Object.keys(reactions[reaction]).length <= 2
          }"
        >
          <img
            :src="EmojiData[reaction].src"
            :alt="EmojiData[reaction].alt"
            class="reactions__item-image"
            @click="onSelectReaction(reaction)"
          />
          <div
            v-if="Object.keys(reactions[reaction]).length <= 2"
            class="reactions__item-avatars"
            @click="onShowListOfUsers"
          >
            <Avatar
              v-for="user in reactions[reaction]"
              :key="user.id"
              :src="user.picture?.pictureUrl ?? null"
              :gap="getInitials(`${user.firstName} ${user.lastName}`)"
              size="sm"
              class="reactions__item-avatar"
            />
          </div>
          <transition
            v-else
            name="fade-reactions"
          >
            <BaseSubHeading
              :key="Object.keys(reactions[reaction]).length"
              weight="500"
              size="small"
              color="monochrome-06"
              class="reactions__item-count"
              @click="onShowListOfUsers"
            >
              {{ Object.keys(reactions[reaction]).length }}
            </BaseSubHeading>
          </transition>
        </div>
      </transition-group>
    </template>
    <template #tooltip-content>
      <div class="custom-scroll reactions__user-list">
        <template
          v-for="reaction in reactionKeys"
          :key="reaction"
        >
          <div
            v-for="(user, index) in reactions[reaction]"
            :key="index"
            class="reactions__user"
          >
            <BaseSubHeading
              weight="400"
              size="medium"
              color="monochrome-06"
            >
              {{ user.firstName }} {{ user.lastName }}
            </BaseSubHeading>
            <img
              :src="EmojiData[reaction].src"
              :alt="EmojiData[reaction].alt"
              class="reactions__user-image"
            />
          </div>
        </template>
      </div>
    </template>
  </DropdownTooltip>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue"

import Avatar from "@/components/Avatar/index.vue"
import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"

import { EmojiData, EmojiType, User } from "@/models"
import { getInitials } from "@/utils/helpers"

import BaseSubHeading from "../Base/BaseSubHeading.vue"

const props = defineProps({
  reactions: {
    type: Object as () => { [key: EmojiType]: User[] },
    required: true
  },
  myReactionType: {
    type: String as () => EmojiType | null,
    default: null
  }
})
let shownUserList = ref(false)

const emit = defineEmits(["onSelectReaction"])
const reactionKeys = computed(() => {
  return Object.keys(props.reactions) as EmojiType[]
})

function onSelectReaction(reaction: EmojiType | null) {
  emit("onSelectReaction", reaction)
}

function onShowListOfUsers() {
  shownUserList.value = true
}

function onHideListOfUsers() {
  shownUserList.value = false
}
</script>

<style lang="scss">
.reactions__popper {
  .v-popper__inner {
    padding-right: 0;
  }
}
</style>

<style scoped lang="scss">
@import "@/assets/scss/utils/mixins/_flex";

.reactions {
  display: flex;

  &__list {
    @include flex;

    flex-wrap: wrap;
    gap: var(--step1);
  }

  &__item {
    @include flex;

    padding: 2px 0 2px var(--step2);
    border: 1px solid var(--monochrome-03);
    border-radius: var(--step4);
    cursor: pointer;

    &:hover,
    &.is-selected {
      border-color: var(--accent-03);
      background: var(--monochrome-01);
    }

    &.has-avatars {
      padding-right: 2px;
      gap: var(--step1);
    }
  }

  &__item-image,
  &__user-image {
    width: var(--icon-16);
    height: var(--icon-16);
  }

  &__item-avatars {
    @include flex;
  }

  &__item-count {
    @include flex($align: center);

    min-height: var(--user-pic-24);
    padding: 0 var(--step2) 0 var(--step1);
    cursor: pointer;
  }

  &__item-avatar {
    position: relative;

    &:not(:first-child) {
      margin-left: -6px;
    }
  }

  &__user {
    @include flex($justify: space-between);

    gap: var(--step2);
  }

  &__user-list {
    @include flex($direction: column);

    max-height: 132px;
    gap: var(--step2);
    overflow-y: scroll;
  }
}

.fade-reactions-enter-active {
  transition: all 0.3s ease-out;
  transition-delay: 0.3s;
}

.fade-reactions-enter-from {
  position: fixed;
  opacity: 0;
  transform: translateY(10px);
}

.dark {
  .reactions {
    &__item {
      border-color: var(--monochrome-36);

      &:hover,
      &.is-selected {
        border-color: var(--accent-200);
        background: var(--monochrome-16);
      }
    }
  }
}
</style>
